import React, { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Button, Select } from '../../../../../../components';
import { icons } from '../../../../../../icons';
import { HeadLine3 } from '../../../../../../components/typography/styled';
import { billingService } from '../../../../../../api';
import { useUi } from '../../../../../../context/ui.context';
import ApiClient from '../../../../../../api/apiClient';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  gap: 20px;
  min-width: 400px;
  max-width: 500px;
  background: ${({ theme }) => theme.palette[10]};
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
`;

const HeadWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Icon = styled.img`
  width: 24px;
  height: 24px;
  cursor: pointer;
  &:hover {
    opacity: 0.7;
  }
`;

const BodyModal = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 12px;
`;

interface ChangePricingPlanModalProps {
  service: any;
  onSuccess: () => void;
  onCancel: () => void;
}

const ChangePricingPlanModal: FC<ChangePricingPlanModalProps> = ({
  service,
  onSuccess,
  onCancel,
}) => {
  const [t] = useTranslation();
  const { viewAlert } = useUi();
  const [plans, setPlans] = useState<any[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);

  const currentPlan = service?.pricing_plan;

  useEffect(() => {
    ApiClient.get('openstack/pricingplans', { params: { page_size: 1000 } })
      .then((res: any) => {
        const allPlans = res.data?.objects || [];
        const filtered = allPlans.filter(
          (p: any) => !currentPlan || p.id !== currentPlan.id
        );
        setPlans(filtered);
      })
      .catch(() => {
        setPlans([]);
      })
      .finally(() => {
        setDataLoading(false);
      });
  }, [service]);

  const handleChangePlan = async () => {
    if (!selectedPlanId || !service) return;
    setLoading(true);
    try {
      await billingService.updateBillingPlan(service.id, selectedPlanId);
      viewAlert({
        severity: 'success',
        message: t("PRICING_PLAN_CHANGED"),
      });
      onSuccess();
    } catch (e: any) {
      viewAlert({
        severity: 'error',
        message: e?.response?.data?.detail || t("FAILED_TO_CHANGE_PRICING_PLAN"),
      });
    } finally {
      setLoading(false);
    }
  };

  const planOptions = plans.map((p: any) => ({
    value: p.id.toString(),
    label: p.name,
  }));

  return (
    <Container>
      <HeadWrapper>
        <HeadLine3 style={{ marginLeft: '10px', marginTop: '10px' }}>
          {t("CHANGE_PRICING_PLAN")}
        </HeadLine3>
        <Icon onClick={onCancel} src={icons.CloseIcon} />
      </HeadWrapper>
      <BodyModal>
        {dataLoading ? (
          <div>{t("LOADING")}...</div>
        ) : (
          <>
            {currentPlan && (
              <p style={{ margin: 0, fontSize: '14px' }}>
                {t("CURRENT_PLAN")}: {currentPlan.name}
              </p>
            )}
            {plans.length === 0 ? (
              <p style={{ margin: 0, fontSize: '14px', opacity: 0.6 }}>
                {t("NO_OTHER_PLANS")}
              </p>
            ) : (
              <Select
                title={t("NEW_PLAN")}
                data={planOptions}
                selectedValue={selectedPlanId}
                onChange={(e: any) => setSelectedPlanId(e)}
                toolTip={false}
              />
            )}
          </>
        )}
        <ButtonsWrapper>
          <Button
            variant="stroke"
            size="display"
            onClick={onCancel}
            disabled={loading}
            title={t("CANCEL")}
          />
          <Button
            variant="primary"
            size="display"
            onClick={handleChangePlan}
            loading={loading}
            disabled={!selectedPlanId || plans.length === 0 || dataLoading}
            title={t("CHANGE_PRICING_PLAN")}
          />
        </ButtonsWrapper>
      </BodyModal>
    </Container>
  );
};

export default ChangePricingPlanModal;
